/**
 * Analytics API Contract
 *
 * ts-rest contract definition for analytics endpoints.
 * This contract ensures type safety between frontend and backend.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for analytics query parameters
 *
 * Validates query parameters for all analytics endpoints with strict rules:
 * - dateRange: Must be one of the predefined time ranges
 * - jobType, assignee, priority: Must be "all" or a numeric ID
 * - Maximum length of 100 characters for string fields
 */
export const analyticsQuerySchema = z.object({
  dateRange: z
    .enum(['last30', 'last90', 'last6m', 'ytd', 'all'])
    .optional()
    .describe('Time range for analytics data'),

  jobType: z
    .string()
    .max(100, 'Job type value too long')
    .refine((val) => val === 'all' || /^\d+$/.test(val), {
      message: 'Job type must be "all" or a numeric job category ID',
    })
    .optional()
    .describe('Job category filter (all or numeric ID)'),

  assignee: z
    .string()
    .max(100, 'Assignee value too long')
    .refine((val) => val === 'all' || /^\d+$/.test(val), {
      message: 'Assignee must be "all" or a numeric user ID',
    })
    .optional()
    .describe('User assignment filter (all or numeric ID)'),

  priority: z
    .string()
    .max(100, 'Priority value too long')
    .refine((val) => val === 'all' || /^\d+$/.test(val), {
      message: 'Priority must be "all" or a numeric priority ID',
    })
    .optional()
    .describe('Priority level filter (all or numeric ID)'),
});

/**
 * Zod schema for Dashboard KPIs response
 */
export const dashboardKpisResponseSchema = z.object({
  totals: z.object({
    totalJobs: z.number(),
    completed: z.number(),
    inProgress: z.number(),
    pending: z.number(),
    urgent: z.number(),
    aborted: z.number(),
    totalValue: z.number(),
    avgValuePerJob: z.number(),
    completionRatePct: z.number(),
    workPackages: z.number(),
    bundledJobs: z.number(),
  }),
  deltas: z.object({
    totalJobsVsPrevPct: z.number().nullable(),
    completedVsPrevPct: z.number().nullable(),
    totalValueVsPrev: z.number().nullable(),
    urgentVsPrev: z.number().nullable(),
  }),
  updatedAt: z.string(),
});

/**
 * Zod schema for Jobs Analytics response
 */
export const jobsAnalyticsResponseSchema = z.object({
  performanceOverTime: z.array(
    z.object({
      period: z.string(),
      newJobs: z.number(),
      completed: z.number(),
      aborted: z.number(),
    }),
  ),
  statusDistribution: z.array(
    z.object({
      status: z.string(),
      count: z.number(),
    }),
  ),
  jobsByType: z.array(
    z.object({
      type: z.string(),
      count: z.number(),
    }),
  ),
  completedVsAbortedByPeriod: z.array(
    z.object({
      period: z.string(),
      completed: z.number(),
      aborted: z.number(),
    }),
  ),
  updatedAt: z.string(),
});

/**
 * Zod schema for Performance Analytics response
 */
export const performanceAnalyticsResponseSchema = z.object({
  workPackageVolumes: z.array(z.any()), // TODO: Define schema when work_package available
  totalValueByJobType: z.array(
    z.object({
      type: z.string(),
      value: z.number(),
    }),
  ),
  teamPerformance: z.array(
    z.object({
      name: z.string(),
      completed: z.number(),
      inProgress: z.number(),
      utilisation: z.number(),
    }),
  ),
  valueTrend: z.array(
    z.object({
      period: z.string(),
      value: z.number(),
    }),
  ),
  updatedAt: z.string(),
});

/**
 * Analytics API Contract
 *
 * Base path: /api/analytics
 */
export const analyticsContract = c.router({
  getDashboardKpis: {
    method: 'GET',
    path: '/dashboard',
    query: analyticsQuerySchema,
    responses: {
      200: dashboardKpisResponseSchema,
      400: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get dashboard KPIs with totals and deltas',
  },
  getJobsAnalytics: {
    method: 'GET',
    path: '/jobs',
    query: analyticsQuerySchema,
    responses: {
      200: jobsAnalyticsResponseSchema,
      400: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get jobs analytics including performance over time and status distribution',
  },
  getPerformanceAnalytics: {
    method: 'GET',
    path: '/performance',
    query: analyticsQuerySchema,
    responses: {
      200: performanceAnalyticsResponseSchema,
      400: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary:
      'Get performance analytics including work packages, team performance, and value trends',
  },
});

/**
 * TypeScript types inferred from the contract
 */
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;
export type DashboardKpisResponse = z.infer<typeof dashboardKpisResponseSchema>;
export type JobsAnalyticsResponse = z.infer<typeof jobsAnalyticsResponseSchema>;
export type PerformanceAnalyticsResponse = z.infer<typeof performanceAnalyticsResponseSchema>;
